import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import '@/App.css';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import AuthCallback from './pages/AuthCallback';
import ProtectedRoute from './components/ProtectedRoute';
import InstallPWA from './components/InstallPWA';
import FloatingConsultation from './components/FloatingConsultation';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Regions = lazy(() => import('./pages/Regions'));
const RegionDetail = lazy(() => import('./pages/RegionDetail'));
const Events = lazy(() => import('./pages/Events'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Forum = lazy(() => import('./pages/Forum'));
const ForumPost = lazy(() => import('./pages/ForumPost'));
const AIChat = lazy(() => import('./pages/AIChat'));
const FamilySchooling = lazy(() => import('./pages/FamilySchooling'));
const SchoolFinder = lazy(() => import('./pages/SchoolFinder'));
const SchoolReviews = lazy(() => import('./pages/SchoolReviews'));
const CostCalculator = lazy(() => import('./pages/CostCalculator'));
const RegionCompare = lazy(() => import('./pages/RegionCompare'));
const RelocationPlanner = lazy(() => import('./pages/RelocationPlanner'));
const SubscriptionSuccess = lazy(() => import('./pages/SubscriptionSuccess'));
const ExpatStories = lazy(() => import('./pages/ExpatStories'));
const StoryDetail = lazy(() => import('./pages/StoryDetail'));
const CreateStory = lazy(() => import('./pages/CreateStory'));
const Messages = lazy(() => import('./pages/Messages'));
const ChatRooms = lazy(() => import('./pages/ChatRooms'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const LanguageResources = lazy(() => import('./pages/LanguageResources'));
const VisaGuide = lazy(() => import('./pages/VisaGuide'));
const VisaSolutions = lazy(() => import('./pages/VisaSolutions'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const PosterDownloads = lazy(() => import('./pages/PosterDownloads'));
const CountryCompare = lazy(() => import('./pages/CountryCompare'));
const CostCompare = lazy(() => import('./pages/CostCompare'));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-primary text-lg">Loading...</div>
  </div>
);

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />

        {/* All other routes require signup */}
        <Route path="/regions" element={<ProtectedRoute><Regions /></ProtectedRoute>} />
        <Route path="/regions/:regionId" element={<ProtectedRoute><RegionDetail /></ProtectedRoute>} />
        <Route path="/region/:regionId" element={<ProtectedRoute><RegionDetail /></ProtectedRoute>} />
        <Route path="/events" element={<ProtectedRoute><Events /></ProtectedRoute>} />
        <Route path="/family" element={<ProtectedRoute><FamilySchooling /></ProtectedRoute>} />
        <Route path="/school-finder" element={<ProtectedRoute><SchoolFinder /></ProtectedRoute>} />
        <Route path="/school-reviews" element={<ProtectedRoute><SchoolReviews /></ProtectedRoute>} />
        <Route path="/cost-calculator" element={<ProtectedRoute><CostCalculator /></ProtectedRoute>} />
        <Route path="/compare" element={<ProtectedRoute><RegionCompare /></ProtectedRoute>} />
        <Route path="/relocation-planner" element={<ProtectedRoute><RelocationPlanner /></ProtectedRoute>} />
        <Route path="/stories" element={<ProtectedRoute><ExpatStories /></ProtectedRoute>} />
        <Route path="/stories/:storyId" element={<ProtectedRoute><StoryDetail /></ProtectedRoute>} />
        <Route path="/language" element={<ProtectedRoute><LanguageResources /></ProtectedRoute>} />
        <Route path="/visa-guide" element={<ProtectedRoute><VisaGuide /></ProtectedRoute>} />
        <Route path="/visa-solutions" element={<ProtectedRoute><VisaSolutions /></ProtectedRoute>} />
        <Route path="/pricing" element={<ProtectedRoute><Pricing /></ProtectedRoute>} />
        <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/forum" element={<ProtectedRoute><Forum /></ProtectedRoute>} />
        <Route path="/forum/:postId" element={<ProtectedRoute><ForumPost /></ProtectedRoute>} />
        <Route path="/chat" element={<ProtectedRoute><AIChat /></ProtectedRoute>} />
        <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
        <Route path="/stories/create" element={<ProtectedRoute><CreateStory /></ProtectedRoute>} />
        <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
        <Route path="/chatrooms" element={<ProtectedRoute><ChatRooms /></ProtectedRoute>} />
        <Route path="/posters" element={<ProtectedRoute><PosterDownloads /></ProtectedRoute>} />
        <Route path="/country-compare" element={<ProtectedRoute><CountryCompare /></ProtectedRoute>} />
        <Route path="/cost-compare" element={<ProtectedRoute><CostCompare /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App paper-texture">
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-center" richColors />
        <InstallPWA />
        <FloatingConsultation />
      </BrowserRouter>
    </div>
  );
}

export default App;
